import { createReducer } from '@mews-framework/redux';
import { GO, GO_BACK, GO_FORWARD, LOCATION_CHANGED, PUSH, REPLACE } from './actions';
import {
    GoAction,
    GoBackAction,
    GoForwardAction,
    LocationChangedAction,
    PushAction,
    ReplaceAction,
    RoutingHistoryMiddlewareAction,
} from './types';

export interface ChangeLocationProps {
    pathname: string;
    search: string;
}

export const push = (href: string): PushAction => ({ type: PUSH, payload: href });
export const replace = (href: string): ReplaceAction => ({ type: REPLACE, payload: href });
export const go = (index: number): GoAction => ({ type: GO, payload: index });
export const goBack = (): GoBackAction => ({ type: GO_BACK });
export const goForward = (): GoForwardAction => ({ type: GO_FORWARD });
export const changeLocation = ({ pathname, search }: ChangeLocationProps): LocationChangedAction => ({
    type: LOCATION_CHANGED,
    payload: { pathname, search },
});

type NavigateAction = (href: string) => PushAction | ReplaceAction;

const navigate =
    (action: NavigateAction) =>
    <P = {}>(routeName: string, params?: P) =>
    (
        dispatch: (action: RoutingHistoryMiddlewareAction) => void,
        getState: unknown,
        { resolveRoute }: { resolveRoute: (routeName: string, params?: P) => string }
    ) => {
        const url = resolveRoute(routeName, params);
        dispatch(action(url));
    };

export const showRoute = navigate(push);
export const replaceRoute = navigate(replace);

export const stringifyLocation = ({ pathname, search }: ChangeLocationProps) =>
    pathname && search ? `${pathname}${search}` : pathname;

export interface RoutingHistoryState {
    pathname: string;
    search: string;
}

export type State<P extends string> = {
    [key in P]: RoutingHistoryState;
};

interface CreateSelectorsOptions<P extends string> {
    path: P;
}

const INITIAL_STATE = {};

export const reducer = createReducer(INITIAL_STATE, {
    [LOCATION_CHANGED]: <P extends string>(state: State<P>, { pathname, search }: ChangeLocationProps) => ({
        ...state,
        pathname,
        search,
    }),
});

export const createSelectors = <P extends string, S extends State<P>>({ path }: CreateSelectorsOptions<P>) => {
    const getState = (state: S) => state[path];

    const getPathname = (state: S) => getState(state).pathname;
    const getSearch = (state: S) => getState(state).search;
    const getLocation = (state: S) => {
        const pathname = getPathname(state);
        const search = getSearch(state);
        return stringifyLocation({ pathname, search });
    };

    return {
        getPathname,
        getSearch,
        getLocation,
    };
};
